<template>
  <div class="pa-settings">
    <!-- Campaign controls (design pa-app.jsx SettingsTab, admin only): pause / resume the package -->
    <div v-if="showCampaignControls" class="pa-panel pa-panel--admin pa-admin pa-settings__controls">
      <h3 class="pa-panel__title">Campaign controls (admin)</h3>
      <div class="pa-admin__row">
        <span class="pa-admin__label">{{ packageName }} · {{ subscriptionLabel }}</span>
        <button
          v-if="isPaused"
          type="button"
          class="pa-admin__trigger"
          :disabled="busy"
          @click="resumeProject"
        >
          <i class="material-icons" aria-hidden="true">play_arrow</i>
          Resume project
        </button>
        <button
          v-else
          type="button"
          class="pa-admin__trigger"
          :disabled="busy"
          @click="pauseProject"
        >
          <i class="material-icons" aria-hidden="true">pause</i>
          Pause project
        </button>
      </div>
      <div class="pa-admin__hint">
        Same controls as the package panel on the Overview; the client sees the paused state there.
      </div>
    </div>

    <!-- Delete is refused by the backend while the package subscription is open (Stripe would keep billing) -->
    <div v-if="deleteBlocked" class="pa-note pa-settings__note">
      <i class="material-icons" aria-hidden="true">info</i>
      <span>
        Deleting this project is unavailable while its POP Authority package is live. Cancel the
        subscription from the <router-link :to="overviewRoute">Overview</router-link> first<template
          v-if="isAdmin"
        >, or close the project (admin)</template>.
      </span>
    </div>

    <!-- The White Glove settings tab as is (Search Console, page mapping, tracked pages, details, danger zone) -->
    <settings-white-glove />
  </div>
</template>

<script>
import SettingsWhiteGlove from '@/components/SitesSingleView/WhiteGlove/Settings.vue'
import {
  POP_AUTHORITY_LIVE_SUBSCRIPTION_STATUSES,
  popAuthorityDeleteBlocked,
  popAuthorityPackageFromStatus,
  popAuthoritySubscriptionStatusLabel,
  showPopAuthorityToast,
} from '@/utils/popAuthority'

/**
 * POP Authority Settings tab (Phase 9): the White Glove settings tab (Search Console connection,
 * report frequency, tracked pages, project details, delete) plus the package-aware bits: the
 * admin "Campaign controls" panel (Phase 5 pause / resume) and the delete note while the package
 * subscription is open. The `?anchor=wg-gsc` scroll contract lives inside the White Glove shell.
 */
export default {
  name: 'SettingsPopAuthority',
  components: { SettingsWhiteGlove },
  inject: {
    refreshWhiteGloveOrders: { default: null },
    getPopAuthoritySiteStatus: { default: null },
  },
  data () {
    return {
      busy: false,
    }
  },
  computed: {
    siteId () {
      const id = parseInt(this.$route.params.siteId, 10)
      return Number.isFinite(id) ? id : null
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    isAdmin () {
      return this.isMissionControlSession && Boolean(this.$store.state.isWhiteGloveAdminMode)
    },
    /** This site's status row from the layout (package, subscription status). */
    status () {
      return this.getPopAuthoritySiteStatus ? this.getPopAuthoritySiteStatus() : null
    },
    paPackage () {
      return popAuthorityPackageFromStatus(this.status)
    },
    packageIsLive () {
      const p = this.paPackage
      return Boolean(p) && !p.provisional && POP_AUTHORITY_LIVE_SUBSCRIPTION_STATUSES.includes(p.subscription_status)
    },
    isPaused () {
      return Boolean(this.paPackage) && this.paPackage.subscription_status === 'paused'
    },
    showCampaignControls () {
      return this.isAdmin && this.packageIsLive
    },
    deleteBlocked () {
      return popAuthorityDeleteBlocked(this.paPackage)
    },
    packageName () {
      return (this.paPackage && this.paPackage.name) || 'Package'
    },
    subscriptionLabel () {
      const p = this.paPackage || {}
      return popAuthoritySubscriptionStatusLabel({ status: p.subscription_status, cancelAtPeriodEnd: p.cancel_at_period_end })
    },
    overviewRoute () {
      return { name: 'OverviewWhiteGlove', params: { siteId: String(this.$route.params.siteId) } }
    },
  },
  methods: {
    /** Package subscription actions (Stripe side effects on the backend); the layout reloads the statuses. */
    runAction (action, successText) {
      if (this.busy || !this.siteId) return Promise.resolve(null)
      this.busy = true
      this.$store.commit('showLoading')
      return this.$store
        .dispatch(action, { siteId: this.siteId })
        .then((response) => {
          const data = response && response.data
          if (data && data.status === 'SUCCESS') {
            showPopAuthorityToast(this, successText)
            return this.refreshWhiteGloveOrders ? this.refreshWhiteGloveOrders() : null
          }
          this.$notify({ group: 'info', type: 'error', text: (data && data.msg) || 'Something went wrong' })
          return null
        })
        .catch((error) => {
          const msg = error?.response?.data?.msg || 'Something went wrong'
          this.$notify({ group: 'info', type: 'error', text: msg })
          return null
        })
        .finally(() => {
          this.$store.commit('hideLoading')
          this.busy = false
        })
    },
    pauseProject () {
      this.runAction('pausePopAuthoritySubscription', 'Subscription paused')
    },
    resumeProject () {
      this.runAction('resumePopAuthoritySubscription', 'Subscription resumed')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup/setup.scss';

.pa-settings {
  &__controls {
    margin: 4px 0 16px;
  }

  &__note {
    max-width: 960px;

    a {
      color: $pa-info;
      font-weight: 700;
    }
  }
}

/* Admin panel (same rules as PopAuthority/Overview.vue .pa-panel / .pa-admin) */
.pa-panel {
  border: 1px solid $pa-border-soft;
  border-radius: 12px;
  background: #fff;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: $pa-teal;

  &__title {
    margin: 0 0 4px;
    font-size: 1.05rem;
    font-weight: 800;
  }

  /* admin-only panels: distinct purple (pa.css .pa-adm) */
  &--admin.pa-admin {
    background: #fbf3fd;
    border-color: #c9a3d6;
  }
}

.pa-admin {
  gap: 12px;

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__label {
    font-size: 0.85rem;
    font-weight: 700;
    color: $pa-muted;
  }

  &__trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #c9a3d6;
    background: #fff;
    color: #7a3e8f;
    border-radius: 8px;
    padding: 6px 10px;
    font-weight: 700;
    font-size: 0.85rem;
    cursor: pointer;
    font-family: inherit;

    .material-icons {
      font-size: 16px;
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  &__hint {
    font-size: 0.8rem;
    color: $pa-muted;
  }
}
</style>
